import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Icon, type IconName } from "../icon";
import {
  MenuItemLayout,
  type MenuSurfaceProps,
  menuItemClassName,
  menuSubHeaderClassName,
  menuSurfaceClassName,
} from "./menu-internals";

export type MenuProps = React.ComponentProps<typeof MenuPrimitive.Root>;

export interface MenuTriggerProps {
  /** A single focusable trigger element, such as Button or IconButton. */
  render: React.ReactElement;
}

export interface MenuPopupProps extends Omit<MenuSurfaceProps, "children"> {
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  alignOffset?: number;
  positionerClassName?: string;
}

const MENU_POPUP_PADDING = 4;
const SUBMENU_SIDE_OFFSET = 13;

interface MenuItemBaseProps
  extends Omit<
    React.ComponentProps<typeof MenuPrimitive.Item>,
    "className" | "children"
  > {
  children: React.ReactNode;
  leadingIcon?: IconName;
  tone?: "default" | "destructive";
  className?: string;
}

type MenuItemTrailingProps =
  | { endSection?: React.ReactNode; submenu?: never }
  | { endSection?: never; submenu: React.ReactNode };

export type MenuItemProps = MenuItemBaseProps & MenuItemTrailingProps;

export interface MenuRadioGroupProps
  extends Omit<
    React.ComponentProps<typeof MenuPrimitive.RadioGroup>,
    "defaultValue" | "className"
  > {
  children: React.ReactNode;
}

export interface MenuRadioItemProps
  extends Omit<
    React.ComponentProps<typeof MenuPrimitive.RadioItem>,
    "className" | "children"
  > {
  children: React.ReactNode;
  leadingIcon?: IconName;
  endSection?: React.ReactNode;
  className?: string;
}

export interface MenuCheckboxItemProps
  extends Omit<
    React.ComponentProps<typeof MenuPrimitive.CheckboxItem>,
    "className" | "children" | "defaultChecked"
  > {
  checked: boolean;
  children: React.ReactNode;
  leadingIcon?: IconName;
  endSection?: React.ReactNode;
  className?: string;
}

export interface MenuSubHeaderProps
  extends Omit<React.ComponentProps<"div">, "className"> {
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

function Menu(props: MenuProps) {
  return <MenuPrimitive.Root {...props} />;
}

/**
 * Renders a supplied focusable trigger as the menu trigger.
 *
 * @example
 * <MenuTrigger render={<IconButton icon="user" aria-label="Open profile menu" />} />
 */
function MenuTrigger({ render }: MenuTriggerProps) {
  return <MenuPrimitive.Trigger render={render} />;
}

function MenuPopup({
  children,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignOffset = 0,
  size,
  className,
  positionerClassName,
  ...props
}: MenuPopupProps) {
  const portalHost = usePortalHost();

  return (
    <MenuPrimitive.Portal container={portalHost}>
      <MenuPrimitive.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={positionerClassName}
      >
        <MenuPrimitive.Popup
          className={menuSurfaceClassName({ size, className })}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

function MenuItem({
  children,
  leadingIcon,
  endSection,
  submenu,
  tone,
  className,
  ...props
}: MenuItemProps) {
  if (submenu != null) {
    const { closeOnClick: _closeOnClick, ...triggerProps } = props;

    return (
      <MenuPrimitive.SubmenuRoot>
        <MenuPrimitive.SubmenuTrigger
          className={menuItemClassName({ tone, className })}
          {...triggerProps}
        >
          <MenuItemLayout leadingIcon={leadingIcon} submenu>
            {children}
          </MenuItemLayout>
        </MenuPrimitive.SubmenuTrigger>
        <MenuPopup
          side="right"
          align="start"
          sideOffset={SUBMENU_SIDE_OFFSET}
          alignOffset={-MENU_POPUP_PADDING}
          size="sm"
        >
          {submenu}
        </MenuPopup>
      </MenuPrimitive.SubmenuRoot>
    );
  }

  return (
    <MenuPrimitive.Item
      className={menuItemClassName({ tone, className })}
      {...props}
    >
      <MenuItemLayout leadingIcon={leadingIcon} endSection={endSection}>
        {children}
      </MenuItemLayout>
    </MenuPrimitive.Item>
  );
}

function MenuRadioGroup({ children, ...props }: MenuRadioGroupProps) {
  return (
    <MenuPrimitive.RadioGroup {...props}>{children}</MenuPrimitive.RadioGroup>
  );
}

function MenuRadioItem({
  children,
  leadingIcon,
  endSection,
  className,
  ...props
}: MenuRadioItemProps) {
  return (
    <MenuPrimitive.RadioItem
      className={menuItemClassName({ className })}
      {...props}
    >
      {leadingIcon && (
        <span className="inline-flex shrink-0 text-menu-icon-default">
          <Icon name={leadingIcon} size="xs" tone="inherit" />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <MenuPrimitive.RadioItemIndicator className="inline-flex shrink-0 text-menu-icon-secondary">
        <Icon name="check-mark" size="xs" tone="inherit" />
      </MenuPrimitive.RadioItemIndicator>
      {endSection && (
        <span className="inline-flex shrink-0 items-center gap-(--spacing-4)">
          {endSection}
        </span>
      )}
    </MenuPrimitive.RadioItem>
  );
}

function MenuCheckboxItem({
  children,
  leadingIcon,
  endSection,
  checked,
  className,
  ...props
}: MenuCheckboxItemProps) {
  return (
    <MenuPrimitive.CheckboxItem
      checked={checked}
      className={menuItemClassName({ className })}
      {...props}
    >
      <MenuItemLayout
        leadingIcon={leadingIcon}
        endSection={endSection}
        checked={checked}
        checkbox
      >
        {children}
      </MenuItemLayout>
    </MenuPrimitive.CheckboxItem>
  );
}

function MenuSubHeader({
  children,
  disabled,
  className,
  ...props
}: MenuSubHeaderProps) {
  return (
    <div
      role="presentation"
      data-disabled={disabled ? "" : undefined}
      aria-disabled={disabled || undefined}
      className={cn(menuSubHeaderClassName, className)}
      {...props}
    >
      {children}
    </div>
  );
}

export {
  Menu,
  MenuCheckboxItem,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSubHeader,
  MenuTrigger,
};
